import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Shield, Users, DollarSign, Dumbbell, Lightbulb, TrendingUp, LogOut, ChevronRight, UserPlus, CreditCard, Activity } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    const headers = getAuthHeaders();
    if (!headers) return;

    axios.get(`${API}/admin/stats`, { headers })
      .then(res => setStats(res.data))
      .catch(err => {
        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.removeItem("admin_token");
          navigate("/admin");
        }
        toast.error("Kunne ikke hente statistik");
      })
      .finally(() => setLoading(false));
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  const statCards = stats ? [
    { label: "Total Brugere", value: stats.users.total, icon: Users, color: "text-blue-400", bg: "from-blue-500/20" },
    { label: "Aktive Prøveperioder", value: stats.users.trial_active, icon: Activity, color: "text-[#DFFF00]", bg: "from-[#DFFF00]/20" },
    { label: "Betalende Abonnenter", value: stats.users.subscribed, icon: CreditCard, color: "text-emerald-400", bg: "from-emerald-500/20" },
    { label: "Udløbne Brugere", value: stats.users.expired, icon: Users, color: "text-red-400", bg: "from-red-500/20" },
    { label: "Nye Denne Uge", value: stats.users.recent_signups, icon: UserPlus, color: "text-purple-400", bg: "from-purple-500/20" },
    { label: "Total Omsætning", value: `${stats.revenue.total_dkk} kr`, icon: DollarSign, color: "text-orange-400", bg: "from-orange-500/20" },
  ] : [];

  const quickLinks = [
    { to: "/admin/users", label: "Bruger Administration", icon: Users, desc: "Se og administrer alle brugere" },
    { to: "/admin/exercises", label: "Øvelser", icon: Dumbbell, desc: "Tilføj og rediger øvelser" },
    { to: "/admin/inspiration", label: "Inspiration", icon: Lightbulb, desc: "Administrer inspiration artikler" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#09090b] via-[#0c0c0f] to-[#09090b]">
      {/* Admin Navbar */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-red-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-red-500 flex items-center justify-center">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <span style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-lg uppercase tracking-wider text-white">
                Admin <span className="text-red-400">Panel</span>
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/admin/users" className="text-sm text-zinc-400 hover:text-white">Brugere</Link>
              <Link to="/admin/exercises" className="text-sm text-zinc-400 hover:text-white">Øvelser</Link>
              <Link to="/admin/inspiration" className="text-sm text-zinc-400 hover:text-white">Inspiration</Link>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-red-400 hover:text-red-300">
                <LogOut className="h-4 w-4 mr-1" /> Log ud
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-dashboard">
        {/* Header */}
        <div className="mb-8">
          <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-3xl uppercase tracking-tight text-white mb-2">
            Dashboard Oversigt
          </h1>
          <p className="text-zinc-400">Velkommen til FitrackR administration</p>
        </div>

        {/* Stats Grid */}
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="bg-[#18181b] border border-white/5 rounded-xl h-32 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {statCards.map(({ label, value, icon: Icon, color, bg }) => (
              <Card key={label} className={`bg-gradient-to-br ${bg} to-transparent border-white/5 rounded-xl overflow-hidden`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">{label}</p>
                      <p style={{ fontFamily: 'JetBrains Mono, monospace' }} className={`font-bold text-2xl ${color}`}>
                        {value}
                      </p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center ${color}`}>
                      <Icon className="h-6 w-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Content Stats */}
        {stats && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-[#18181b] border border-white/5 rounded-xl p-5 text-center">
              <Dumbbell className="h-6 w-6 text-[#DFFF00] mx-auto mb-2" />
              <p style={{ fontFamily: 'JetBrains Mono, monospace' }} className="text-2xl font-bold text-white">{stats.content.exercises}</p>
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Øvelser</p>
            </div>
            <div className="bg-[#18181b] border border-white/5 rounded-xl p-5 text-center">
              <Lightbulb className="h-6 w-6 text-orange-400 mx-auto mb-2" />
              <p style={{ fontFamily: 'JetBrains Mono, monospace' }} className="text-2xl font-bold text-white">{stats.content.inspiration}</p>
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Inspiration</p>
            </div>
            <div className="bg-[#18181b] border border-white/5 rounded-xl p-5 text-center">
              <TrendingUp className="h-6 w-6 text-emerald-400 mx-auto mb-2" />
              <p style={{ fontFamily: 'JetBrains Mono, monospace' }} className="text-2xl font-bold text-white">{stats.content.workouts_logged}</p>
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Loggede Træninger</p>
            </div>
          </div>
        )}

        {/* Quick Links */}
        <h2 style={{ fontFamily: 'Oswald, sans-serif' }} className="uppercase text-sm tracking-wider text-zinc-500 mb-4">Administration</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {quickLinks.map(({ to, label, icon: Icon, desc }) => (
            <Link to={to} key={to}>
              <div className="bg-[#18181b] border border-white/5 rounded-xl p-5 hover:border-red-500/30 transition-all group">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center group-hover:bg-red-500/20 transition-colors">
                    <Icon className="h-6 w-6 text-red-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-white">{label}</p>
                    <p className="text-xs text-zinc-500">{desc}</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-zinc-600 group-hover:text-red-400 transition-colors" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
